import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { postFormCheck } from '../api/formCheckService';
import { estimateLumaFromCanvas } from '../utils/pose/angles';
import countdownBeepSrc from '../components/sounds/coin.mp3';
import { createWebTranslator } from '../utils/webI18n';

const MODEL_ASSET_URL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';
const WASM_ASSET_ROOT =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.13/wasm';

const FRAME_TARGET = 18;
const CAPTURE_INTERVAL_MS = 300;
const JPEG_QUALITY = 0.7;
const MAX_SAMPLE_ANGLES = 6;

const REQUIRED_POSE_RATIO = 0.5;
const MIN_LUMA = 0.1;

const CAMERA_BASE_CONSTRAINTS = {
  width: { ideal: 720 },
  height: { ideal: 480 }
};

const createPoseWorker = () =>
  new Worker(new URL('../workers/pose.worker.js', import.meta.url), { type: 'module' });

const requestId = (prefix = 'req') =>
  `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const COUNTDOWN_SECONDS = 10;

const INITIAL_STATE = { stage: 'idle' };

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function bytesFromDataUrl(dataUrl) {
  if (!dataUrl) return 0;
  const base64 = dataUrl.split(',')[1];
  if (!base64) return 0;
  return Math.ceil((base64.length * 3) / 4);
}

function buildCameraConstraintCandidates(desiredFacing) {
  const baseVideo = { ...CAMERA_BASE_CONSTRAINTS };
  const candidates = [];

  if (desiredFacing) {
    candidates.push({
      audio: false,
      video: {
        ...baseVideo,
        facingMode: { exact: desiredFacing }
      }
    });
    candidates.push({
      audio: false,
      video: {
        ...baseVideo,
        facingMode: { ideal: desiredFacing }
      }
    });
  }

  candidates.push({
    audio: false,
    video: { ...baseVideo }
  });

  candidates.push({
    audio: false,
    video: true
  });

  return candidates;
}

async function openCameraStream(desiredFacing) {
  if (!navigator?.mediaDevices?.getUserMedia) {
    throw Object.assign(new Error('Camera API unavailable'), { name: 'CameraAPIUnavailable' });
  }

  const candidates = buildCameraConstraintCandidates(desiredFacing);
  let lastError = null;

  for (const constraints of candidates) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const [track] = stream.getVideoTracks();
      const settings = track?.getSettings?.();
      const facingMode = settings?.facingMode || desiredFacing || null;

      const videoEl = document.createElement('video');
      videoEl.srcObject = stream;
      videoEl.playsInline = true;
      videoEl.muted = true;
      try {
        await videoEl.play();
      } catch (playErr) {
        console.warn('[FormCheck] Preview video failed to autoplay', playErr);
      }

      return { stream, facingMode, videoEl };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error('Unable to access camera stream.');
}

export function useFormCheck({ exerciseName, exerciseDescription, onQuotaStateChange, locale }) {
  const [state, setState] = useState(INITIAL_STATE);
  const facingModeRef = useRef(null);
  const bytesCapturedRef = useRef(0);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const countdownAudioRef = useRef(null);
  const countdownControllerRef = useRef(null);
  const t = useMemo(() => createWebTranslator(locale), [locale]);
  const workerRef = useRef(null);
  const pendingRequestsRef = useRef(new Map());
  const workerReadyPromiseRef = useRef(null);

  const consentGiven = useMemo(() => {
    return localStorage.getItem('formCheckConsent') === 'true';
  }, [state.stage]);

  const ensureConsent = useCallback(() => {
    if (!consentGiven) {
      localStorage.setItem('formCheckConsent', 'true');
    }
  }, [consentGiven]);

  const teardownWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    pendingRequestsRef.current.forEach(({ reject }) => {
      try {
        reject(new Error('Pose worker disposed'));
      } catch (err) {
        // ignore rejection failures
      }
    });
    pendingRequestsRef.current.clear();
    workerReadyPromiseRef.current = null;
  }, []);

  const handleWorkerFatalError = useCallback(
    (err) => {
      console.error('[FormCheck] Pose worker error', err);
      teardownWorker();
      setState({
        stage: 'error',
        code: 'E_POSE_WORKER',
        message: 'Form Check is unavailable right now. Please try again.'
      });
    },
    [teardownWorker]
  );

  const ensureWorkerInstance = useCallback(() => {
    if (workerRef.current) {
      return workerRef.current;
    }
    const worker = createPoseWorker();
    worker.onmessage = (event) => {
      const { type, requestId: incomingId, payload, error } = event.data || {};
      if (incomingId && pendingRequestsRef.current.has(incomingId)) {
        const { resolve, reject } = pendingRequestsRef.current.get(incomingId);
        pendingRequestsRef.current.delete(incomingId);
        if (type === 'ERROR') {
          reject(new Error(error || 'Pose worker error'));
        } else {
          resolve(payload);
        }
        return;
      }
      if (type === 'ERROR' && !incomingId) {
        handleWorkerFatalError(new Error(error || 'Pose worker error'));
      }
    };
    worker.onerror = handleWorkerFatalError;
    workerRef.current = worker;
    return worker;
  }, [handleWorkerFatalError]);

  const postToWorker = useCallback(
    (type, payload = {}, transfer = []) => {
      const worker = ensureWorkerInstance();
      const id = requestId(type.toLowerCase());
      return new Promise((resolve, reject) => {
        pendingRequestsRef.current.set(id, { resolve, reject });
        try {
          worker.postMessage({ type, requestId: id, ...payload }, transfer);
        } catch (err) {
          pendingRequestsRef.current.delete(id);
          reject(err);
        }
      });
    },
    [ensureWorkerInstance]
  );

  const ensureWorkerReady = useCallback(() => {
    if (workerReadyPromiseRef.current) {
      return workerReadyPromiseRef.current;
    }
    const promise = postToWorker('INIT', {
      payload: {
        modelAssetPath: MODEL_ASSET_URL,
        wasmAssetRoot: WASM_ASSET_ROOT
      }
    }).catch((err) => {
      workerReadyPromiseRef.current = null;
      throw err;
    });
    workerReadyPromiseRef.current = promise;
    return promise;
  }, [postToWorker]);

  const resetWorkerSession = useCallback(() => postToWorker('RESET'), [postToWorker]);

  const analyzeWithWorker = useCallback(
    (bitmap, frameIdx) => postToWorker('ANALYZE', { frameIdx, bitmap }, [bitmap]),
    [postToWorker]
  );

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause?.();
      } catch (err) {
        // no-op
      }
      videoRef.current.srcObject = null;
      videoRef.current = null;
    }
  }, []);

  const preparePreview = useCallback(async (desiredFacing) => {
    try {
      const preview = await openCameraStream(desiredFacing || facingModeRef.current || 'environment');
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (videoRef.current) {
        try {
          videoRef.current.pause?.();
        } catch (pauseErr) {
          // no-op
        }
        videoRef.current.srcObject = null;
      }

      streamRef.current = preview.stream;
      videoRef.current = preview.videoEl;
      facingModeRef.current = preview.facingMode;

      setState((prev) => ({
        ...prev,
        stage: 'preview',
        cameraFacing: facingModeRef.current,
        previewStream: preview.stream,
        cameraSwitching: false,
        cameraToggleError: null
      }));
    } catch (err) {
      const denied =
        err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
      setState({
        stage: 'error',
        code: denied ? 'E_CAMERA_DENIED' : 'E_CAMERA_UNAVAILABLE',
        message: denied
          ? 'Camera access denied. Please enable camera permissions to run Form Check.'
          : 'Unable to access the camera. Check your device settings and try again.'
      });
    }
  }, []);

  const start = useCallback(async () => {
    ensureConsent();
    setState({ stage: 'initializing' });

    const workerWarmup = ensureWorkerReady().catch((err) => {
      console.warn('[FormCheck] Pose worker init failed', err);
      setState((prev) =>
        prev.stage === 'error'
          ? prev
          : {
              stage: 'error',
              code: 'E_POSE_INIT',
              message: 'Unable to initialize form analysis. Please try again.'
            }
      );
    });

    await preparePreview();
    await workerWarmup;
  }, [ensureConsent, ensureWorkerReady, preparePreview]);

  const updateStateToPreview = useCallback(() => {
    setState((prev) => ({
      ...prev,
      stage: 'preview',
      seconds: undefined,
      cameraFacing: facingModeRef.current,
      previewStream: streamRef.current || prev.previewStream || null,
      cameraSwitching: false
    }));
  }, [streamRef, facingModeRef]);

  const beginCapture = useCallback(async () => {
    if (!streamRef.current || !videoRef.current) {
      setState({
        stage: 'error',
        code: 'E_CAMERA_UNAVAILABLE',
        message: 'Camera preview not available. Please try again.'
      });
      return;
    }

    if (countdownControllerRef.current) {
      // Countdown already active; ignore duplicate request.
      return;
    }

    const controller = { cancelled: false };
    countdownControllerRef.current = controller;

    let countdownAudio = countdownAudioRef.current;
    if (!countdownAudio) {
      countdownAudio = new Audio(countdownBeepSrc);
      countdownAudio.preload = 'auto';
      countdownAudio.volume = 0.65;
      countdownAudioRef.current = countdownAudio;
    }

    setState((prev) => ({
      ...prev,
      stage: 'countdown',
      seconds: COUNTDOWN_SECONDS,
      cameraFacing: facingModeRef.current
    }));

    for (let remaining = COUNTDOWN_SECONDS; remaining > 0; remaining -= 1) {
      if (controller.cancelled) {
        if (countdownAudio) {
          try {
            countdownAudio.pause();
            countdownAudio.currentTime = 0;
          } catch (audioStopErr) {
            // no-op
          }
        }
        countdownControllerRef.current = null;
        updateStateToPreview();
        return;
      }

      setState((prev) => ({
        ...prev,
        stage: 'countdown',
        seconds: remaining,
        cameraFacing: facingModeRef.current
      }));

      if (countdownAudio) {
        try {
          countdownAudio.currentTime = 0;
          const playPromise = countdownAudio.play();
          if (playPromise?.catch) {
            playPromise.catch((err) => {
              console.warn('[FormCheck] Countdown audio failed', err);
            });
          }
        } catch (audioErr) {
          console.warn('[FormCheck] Countdown audio error', audioErr);
        }
      }
      await delay(1000);
      if (controller.cancelled) {
        if (countdownAudio) {
          try {
            countdownAudio.pause();
            countdownAudio.currentTime = 0;
          } catch (audioStopErr) {
            // no-op
          }
        }
        countdownControllerRef.current = null;
        updateStateToPreview();
        return;
      }
    }
    if (countdownAudio) {
      try {
        countdownAudio.pause();
        countdownAudio.currentTime = 0;
      } catch (audioStopErr) {
        // no-op
      }
    }

    if (controller.cancelled) {
      countdownControllerRef.current = null;
      updateStateToPreview();
      return;
    }

    countdownControllerRef.current = null;

    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 480;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      cleanupStream();
      setState({
        stage: 'error',
        code: 'E_CANVAS_CONTEXT',
        message: 'Unable to access camera stream. Please try again.'
      });
      return;
    }

    try {
      await ensureWorkerReady();
      await resetWorkerSession();
    } catch (poseErr) {
      console.warn('[FormCheck] Pose worker not ready', poseErr);
      cleanupStream();
      setState({
        stage: 'error',
        code: 'E_POSE_INIT',
        message: 'Unable to initialize form analysis. Please try again.'
      });
      return;
    }

    const frames = [];
    const analyses = [];
    const lumaSamples = [];
    let totalBytes = 0;

    setState((prev) => ({
      ...prev,
      stage: 'capturing',
      progress: 0,
      cameraFacing: facingModeRef.current,
      previewStream: streamRef.current || prev.previewStream || null
    }));

    for (let idx = 0; idx < FRAME_TARGET; idx += 1) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const luma = estimateLumaFromCanvas(ctx);
      if (luma !== null) {
        lumaSamples.push(luma);
      }

      const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      const bytes = bytesFromDataUrl(dataUrl);
      totalBytes += bytes;

      if (bytes > 350000) {
        console.warn('[FormCheck] Large frame detected (>350KB); consider adjusting constraints.');
      }

      frames.push(dataUrl);

      if (idx % 3 === 0) {
        try {
          const bitmap = await createImageBitmap(canvas);
          const analysis = await analyzeWithWorker(bitmap, idx);
          analyses.push(analysis);
        } catch (poseErr) {
          console.warn('[FormCheck] Pose analysis failed', poseErr);
        }
      }

      setState((prev) => ({
        ...prev,
        stage: 'capturing',
        progress: (idx + 1) / FRAME_TARGET,
        cameraFacing: facingModeRef.current
      }));

      await delay(CAPTURE_INTERVAL_MS);
    }

    cleanupStream();
    bytesCapturedRef.current = totalBytes;

    const qualitySamples = analyses.filter(Boolean);
    let landmarkOkRatio = qualitySamples.length
      ? qualitySamples.filter((sample) => sample.ok).length / qualitySamples.length
      : 1;
    landmarkOkRatio = Number(landmarkOkRatio.toFixed(2));

    const fullBodyScore = qualitySamples.reduce((acc, sample) => {
      if (!sample?.quality?.visibility) return acc;
      const vis = sample.quality.visibility;
      const requiredIndices = [11, 12, 23, 24, 25, 26];
      const visibleCount = requiredIndices.filter((idx) => (vis[idx] || 0) >= 0.5).length;
      return acc + (visibleCount >= 4 ? 1 : 0);
    }, 0);
    const fullBodyOk = qualitySamples.length ? (fullBodyScore / qualitySamples.length) >= 0.4 : true;

    const averageLuma = lumaSamples.length
      ? lumaSamples.reduce((acc, value) => acc + value, 0) / lumaSamples.length
      : null;

    if (landmarkOkRatio < REQUIRED_POSE_RATIO || !fullBodyOk || (averageLuma !== null && averageLuma < MIN_LUMA)) {
      setState({
        stage: 'error',
        code: 'E_POSE_QUALITY_FAIL',
        message: t(
          'formCheck.qualityReadFailure',
          'We couldn’t get a consistent read on your form. Try stepping back so shoulders and hips are visible and ensure lighting is even.'
        ),
        details: {
          landmarkOkRatio,
          fullBodyOk,
          averageLuma
        }
      });
      return;
    }

    const motionAverage = qualitySamples.length
      ? qualitySamples.reduce((acc, sample) => acc + (sample.motion || 0), 0) /
        qualitySamples.length
      : 0;

    const angleEntries = qualitySamples
      .map((sample) => sample.angleEntry)
      .filter(Boolean)
      .slice(0, MAX_SAMPLE_ANGLES);

    setState({ stage: 'processing' });

    const payload = {
      exercise: {
        name: exerciseName || 'Unknown exercise',
        description: exerciseDescription || ''
      },
      capture: {
        fps_estimate: Math.round(1000 / CAPTURE_INTERVAL_MS),
        frames,
        timestamp_ms: Date.now()
      },
      poseHints: {
        frames_used: frames.length,
        quality_flags: {
          full_body_ok: fullBodyOk,
          lighting_ok: averageLuma === null ? true : averageLuma >= MIN_LUMA,
          motion_ok: motionAverage > 0.002
        },
        angles_sample: angleEntries,
        landmark_ok_ratio: landmarkOkRatio
      },
      clientEnv: {
        device: /Mobi/i.test(navigator.userAgent) ? 'web-mobile' : 'web-desktop',
        viewport: [window.innerWidth, window.innerHeight],
        camera_facing: facingModeRef.current || 'unknown'
      },
      upload_stats: {
        total_bytes_estimate: totalBytes
      }
    };

    try {
      const result = await postFormCheck(payload);
      onQuotaStateChange?.();
      setState({
        stage: 'result',
        data: result,
        upload: {
          bytes: totalBytes,
          frames: frames.length,
          cameraFacing: facingModeRef.current
        }
      });
    } catch (err) {
      console.error('[FormCheck] API error', err);
      const responseMessage = err.response?.data?.message || err.response?.data?.error;
      const responseCode = err.response?.data?.code || err.code;
      onQuotaStateChange?.();
      setState({
        stage: 'error',
        code: responseCode || 'E_SERVER',
        message: responseMessage || err.message || t('formCheck.requestFailed', 'Form check failed. Please try again.')
      });
    }
  }, [
    analyzeWithWorker,
    cleanupStream,
    ensureWorkerReady,
    exerciseDescription,
    exerciseName,
    onQuotaStateChange,
    resetWorkerSession,
    t
  ]);

  const toggleCamera = useCallback(async () => {
    const nextFacing = facingModeRef.current === 'user' ? 'environment' : 'user';
    setState((prev) => ({
      ...prev,
      cameraSwitching: true,
      cameraToggleError: null
    }));

    try {
      const preview = await openCameraStream(nextFacing);
      const previousStream = streamRef.current;
      const previousVideo = videoRef.current;

      streamRef.current = preview.stream;
      videoRef.current = preview.videoEl;
      facingModeRef.current = preview.facingMode;

      if (previousStream) {
        previousStream.getTracks().forEach((track) => track.stop());
      }
      if (previousVideo) {
        try {
          previousVideo.pause?.();
        } catch (pauseErr) {
          // no-op
        }
        previousVideo.srcObject = null;
      }

      setState((prev) => ({
        ...prev,
        stage: 'preview',
        cameraFacing: facingModeRef.current,
        previewStream: preview.stream,
        cameraSwitching: false,
        cameraToggleError: null
      }));
    } catch (err) {
      console.warn('[FormCheck] Camera toggle failed', err);
      setState((prev) => ({
        ...prev,
        cameraSwitching: false,
        cameraToggleError: 'Unable to switch camera. Please stay with the current lens.'
      }));
    }
  }, []);

  const stopCountdown = useCallback(() => {
    const controller = countdownControllerRef.current;
    if (!controller) {
      return;
    }
    controller.cancelled = true;
    if (countdownAudioRef.current) {
      try {
        countdownAudioRef.current.pause();
        countdownAudioRef.current.currentTime = 0;
      } catch (err) {
        // no-op
      }
    }
    updateStateToPreview();
  }, [updateStateToPreview]);

  const cancelPreview = useCallback(() => {
    stopCountdown();
    cleanupStream();
    setState(INITIAL_STATE);
    countdownControllerRef.current = null;
  }, [cleanupStream, stopCountdown]);

  const reset = useCallback(() => {
    stopCountdown();
    cleanupStream();
    setState(INITIAL_STATE);
    bytesCapturedRef.current = 0;
    facingModeRef.current = null;
    countdownControllerRef.current = null;
  }, [cleanupStream, stopCountdown]);

  useEffect(() => {
    return () => {
      cleanupStream();
      teardownWorker();
      if (countdownAudioRef.current) {
        try {
          countdownAudioRef.current.pause();
        } catch (err) {
          // no-op
        }
        countdownAudioRef.current = null;
      }
      if (countdownControllerRef.current) {
        countdownControllerRef.current.cancelled = true;
        countdownControllerRef.current = null;
      }
    };
  }, [cleanupStream, teardownWorker]);

  return {
    state,
    start,
    beginCapture,
    stopCountdown,
    toggleCamera,
    cancelPreview,
    reset,
    consentGiven,
    cameraFacing: state.cameraFacing || facingModeRef.current,
    bytesCaptured: bytesCapturedRef.current,
    previewStream: state.previewStream || null
  };
}
